import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyLabel = '— None —',
  className = '',
  disabled,
  id,
  /** When false, only pick from options (no extra row that clears to "") */
  allowClear = true,
  variant = 'light',
}: {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  allowClear?: boolean;
  variant?: 'light' | 'dark';
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    return options.find((o) => o.value === value)?.label ?? value;
  }, [value, options]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const blanks = options.filter((o) => o.value === '');
    const rest = options.filter((o) => o.value !== '');
    if (!ql) return options;
    const hitRest = rest.filter(
      (o) =>
        o.label.toLowerCase().includes(ql) || o.value.toLowerCase().includes(ql),
    );
    // Keep "none" rows visible while filtering so series/group can be cleared
    return [...blanks, ...hitRest];
  }, [options, q]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const isDark = variant === 'dark';
  const btnCls = isDark
    ? 'flex items-center justify-between gap-2 w-full border border-gray-600 rounded-lg px-3 py-2 text-sm text-left bg-gray-700 text-white hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:pointer-events-none'
    : 'flex items-center justify-between gap-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-left bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:pointer-events-none';

  const panelCls = isDark
    ? 'absolute z-50 mt-1 w-full min-w-[12rem] rounded-lg border border-gray-600 bg-gray-800 shadow-xl overflow-hidden'
    : 'absolute z-50 mt-1 w-full min-w-[12rem] rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden';

  const inputCls = isDark
    ? 'w-full px-3 py-2 text-sm border-b border-gray-700 bg-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500'
    : 'w-full px-3 py-2 text-sm border-b border-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500';

  const optBtn = (active: boolean) =>
    isDark
      ? `w-full px-3 py-1.5 text-left text-sm ${
          active ? 'bg-gray-700 font-medium text-white' : 'text-gray-200 hover:bg-gray-700'
        }`
      : `w-full px-3 py-1.5 text-left text-sm hover:bg-brand-50 ${
          active ? 'bg-brand-50 font-medium text-brand-900' : 'text-gray-800'
        }`;

  const labelCls = isDark ? 'truncate min-w-0 flex-1 text-white' : 'truncate min-w-0 flex-1 text-gray-900';

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        className={btnCls}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={labelCls}>{selectedLabel || placeholder}</span>
        <ChevronDown size={16} className={isDark ? 'text-gray-400 shrink-0' : 'text-gray-400 shrink-0'} />
      </button>
      {open && (
        <div className={panelCls} role="listbox">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter…"
            className={inputCls}
            autoFocus
          />
          <div className="max-h-56 overflow-y-auto py-1">
            {allowClear && (
              <button
                type="button"
                role="option"
                className={
                  isDark
                    ? 'w-full px-3 py-1.5 text-left text-sm text-gray-400 hover:bg-gray-700'
                    : 'w-full px-3 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-50'
                }
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                {emptyLabel}
              </button>
            )}
            {filtered.map((o, oi) => (
              <button
                key={`${o.value}-${oi}-${o.label}`}
                type="button"
                role="option"
                className={optBtn(o.value === value)}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span className="block truncate">{o.label}</span>
                {o.label !== o.value ? (
                  <span
                    className={
                      isDark
                        ? 'block text-[11px] text-gray-500 font-mono truncate'
                        : 'block text-[11px] text-gray-400 font-mono truncate'
                    }
                  >
                    {o.value}
                  </span>
                ) : null}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className={`px-3 py-3 text-sm text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No matches
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
